import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Country, State, City } from 'country-state-city';
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Form from "react-bootstrap/Form";
import { Modal } from "react-bootstrap";
import Image from "react-bootstrap/Image";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import MainWrapper from "../layouts/MainWrapper";
import CardWrapper from "../layouts/CardWrapper";
import Button from "react-bootstrap/Button";
import PageTitle from "../components/PageTitle";
import CustomBreadcrumb from "../components/CustomBreadcrumb";
import { UserCircle, UserCircleCheck } from "../components/SVGIcon";
import { addUsers, deleteUser, singleUser, updateUser } from "../services/userService";
import { licenses } from "../services/licenseService";
import { useAlert } from "../contexts/AlertContext";
import { authAtom } from "../states/auth";
import { useAtom } from "jotai";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import Select from 'react-select';
import { getAllCountries, getCitiesofSelectedState, getStatesofSelectedCountry } from "../services/countryStateService";

const countryOptions = Country.getAllCountries();
const stateDropdownData = State.getStatesOfCountry(countryOptions[0].isoCode);

const validationSchema = Yup.object({
  firstName: Yup.string().trim().required("First name is required"),
  lastName: Yup.string().trim().required("Last name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string()
    .test('is-valid-phone', 'Invalid phone number', function (value) {
      if (!value) return false;
      const phoneNumber = parsePhoneNumberFromString(value);
      return phoneNumber ? phoneNumber.isValid() : false;
    })
    .required("Phone is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Minimum 8 characters")
    .matches(/[a-z]/, "At least one lowercase letter")
    .matches(/[A-Z]/, "At least one uppercase letter")
    .matches(/\d/, "At least one number")
    .matches(/[@$!%*?&#]/, "At least one special character"),
  confirm_password: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("password"), null], "Passwords must match"),
  streetName: Yup.string().required("Street name is required"),
  country: Yup.string().required("Country is required"),
  state: Yup.string().required("State is required"),
  city: Yup.string().required("City is required"),
  zipCode: Yup.string().required("Zip Code is required"),

  userType: Yup.string().required("User type is required"),
});

const editValidationSchema = Yup.object({
  firstName: Yup.string().trim().required("First name is required"),
  lastName: Yup.string().trim().required("Last name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string()
    .test('is-valid-phone', 'Invalid phone number', function (value) {
      if (!value) return false;
      const phoneNumber = parsePhoneNumberFromString(value);
      return phoneNumber ? phoneNumber.isValid() : false;
    })
    .required("Phone is required"),
  streetName: Yup.string().required("Street name is required"),
  country: Yup.string().required("Country is required"),
  state: Yup.string().required("State is required"),
  city: Yup.string().required("City is required"),
  zipCode: Yup.string().required("Zip Code is required"),
  userType: Yup.string().required("User type is required"),
});

const UserForm = () => {
  const { userId } = useParams();
  // const userId = propUserId || paramUserId;
  const [auth] = useAtom(authAtom);
  const [isEditMode, setIsEditMode] = useState(false);
  const [licences, setLicences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [phoneDetails, setPhoneDetails] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState();
  const [stateDropdown, setStateDropdown] = useState(stateDropdownData);
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [companyDetails, setCompanyDetails] = useState([]);
  const [addressDisabled, setAddressDisabled] = useState(true);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedState, setSelectedState] = useState();
  const [initialValues, setInitialValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirm_password: "",
    streetName: "",
    country: { name: countryOptions[0].name, code: countryOptions[0].isoCode },
    state: { name: stateDropdown[0]?.name, code: stateDropdown[0]?.isoCode },
    city: "",
    zipCode: "",
    userType: "",
  });
  const [showModal, setShowModal] = useState(false);
  const stateSetOnCountrychange = (code) => {
    console.log(State.getStatesOfCountry(code));
  }

  useEffect(() => {
    getAllCountries().then((response) => {
      if (response) {
        const options = response.map(c => ({
          label: c.name,
          value: c.isoCode
        }));
        setCountryList(options);
      }
    });
  }, []);

  useEffect(() => {
    if (!selectedCountry) {
      setStateList([]);
      setSelectedState(null);
      return;
    }
    getStatesofSelectedCountry(selectedCountry.value).then((response) => {
      if (response) {
        const options = response.map(s => ({
          label: s.name,
          value: s.isoCode
        }));
        setStateList(options);
      }
    });
    setSelectedState(null);
    setSelectedCity(null);
    setCityList([]);
  }, [selectedCountry]);
  useEffect(() => {
    if (!selectedState) {
      setCityList([]);
      setSelectedCity(null);
      return;
    }
    getCitiesofSelectedState(selectedCountry.value, selectedState.value).then((response) => {
      if (response) {
        const options = response.map(c => ({
          label: c.name,
          value: c.name
        }));
        setCityList(options);
      }
    });
    setSelectedCity(null);
  }, [selectedState]);

  const fetchLicense = async () => {
    try {
      const licencesData = await licenses();
      console.log("licences", licencesData?.data);
      setLicences(licencesData?.data);
    } catch (error) {
      console.error("Error fetching Licences:", error);
    }
  };

  useEffect(() => {
    singleUser(auth?.user?.id).then((response) => {
      const tempUserData = response?.data?.user;
      if (response?.data?.user) {
        setCompanyDetails(tempUserData);
      }
    })
  }, []);

  const deleteUserFunction = async (id) => {
    try {
      const deleteUserData = await deleteUser(id);
      showAlert({
        title: "Users Notification",
        message: deleteUserData?.message ?? "something went wrong",
        variant: "success",
      });
      setShowModal(null);
      setTimeout(() => {
        navigate('/users');
      }, 1500);
    } catch (error) {
      console.error("Error saving user:", error);
      setShowModal(null);
      showAlert({
        title: "Routing Notification",
        message: error?.response?.data?.message ?? "something went wrong",
        variant: "danger",
      });
    }
  };

  const loadUser = async () => {
    if (userId) {
      setIsEditMode(true);
      try {
        let user = await singleUser(userId);
        user = user.data.user;
        setAddressDisabled(user?.isMakeSameAsAdmin);
        setInitialValues({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: (user.countryCode.startsWith('+') ? user.countryCode : '+' + user.countryCode) +
            user.phoneNo || "",
          // password: "",
          // confirm_password: "",
          streetName: user.address || "",
          country: user?.country?.name || countryOptions[0].value,
          state: user?.state?.name || "",
          city: user.city || "",
          zipCode: user.zipCode || "",
          userType: user.roleId || "",
          status: user.status || "",
        });

        setPhone((user.countryCode.startsWith('+') ? user.countryCode : '+' + user.countryCode) + user.phoneNo || "");
        changePhoneNumber((user.countryCode.startsWith('+') ? user.countryCode : '+' + user.countryCode) + user.phoneNo || "");
        const formattedCountries = countryOptions.map(c => ({
          label: c.name,
          value: c.isoCode
        }));

        setCountryList(formattedCountries);
        const countryObj = formattedCountries.find(
          opt => opt.label === user.country?.name || opt.value === user.country?.code
        ) || null;

        setSelectedCountry(countryObj);
        if (countryObj) {
          const statesResp = await getStatesofSelectedCountry(countryObj.value);
          const formattedStates = statesResp.map(s => ({
            label: s.name,
            value: s.isoCode
          }));
          setStateList(formattedStates);

          const stateObj = formattedStates.find(s => s.label === user.state?.name) || null;
          setSelectedState(stateObj);
          if (stateObj) {
            const citiesResp = await getCitiesofSelectedState(countryObj.value, stateObj.value);
            const formattedCities = citiesResp.map(c => ({
              label: c.name,
              value: c.name
            }));
            setCityList(formattedCities);

            const cityObj = formattedCities.find(c => c.label === user.city) || null;
            setSelectedCity(cityObj);
          } else {
            setCityList([]);
            setSelectedCity(null);
          }
        }
      } catch (err) {
        console.error("Error loading user", err);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLicense();
    if (userId) {
      loadUser();
    } else {
      singleUser(auth?.user?.id).then((response) => {
        if (response?.data?.user) {
          const userObj = response?.data?.user;
          setInitialValues({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            password: "",
            confirm_password: "",
            streetName: userObj?.address || "",
            country: userObj?.country?.name || countryOptions[0].value,
            state: userObj?.state?.name || "",
            city: userObj?.city || "",
            zipCode: userObj?.zipCode || "",
            userType: "",
          });
          // setSelectedCountry(
          //   countryOptions.find((c) => c.isoCode === userObj?.country?.code) || countryOptions[0]
          // ); 
          setLoading(false);
        }
      })
    }

  }, []);

  // Update PasswordToggle to handle a single boolean for both fields
  const PasswordToggle = () => {
    setShowPassword((prevState) => !prevState);
  };
  const ConfirmPasswordToggle = () => {
    setShowConfirmPassword((prevState) => !prevState);
  };

  const changePhoneNumber = (value) => {
    console.log(value)
    if (typeof value !== "undefined") {
      const phoneDetailsTemp = parsePhoneNumber(value);
      setPhoneDetails(phoneDetailsTemp);
      setPhone(value);
    }
  };
  console.log('this is countery select', selectedCountry, selectedState, selectedCity);
  const handleSubmit = async (values) => {
    const payload = {
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
      status: values.status,
      phone_no: phoneDetails?.nationalNumber || "",
      country_code: phoneDetails?.countryCallingCode,
      address: values.streetName,
      role_id: values.userType,
      isMakeSameAsAdmin: addressDisabled,
      ...(!addressDisabled
        ? {
          country: selectedCountry?.label,
          state: selectedState?.label,
          city: selectedCity?.label,
          zipCode: values.zipCode,
        }
        : {

        }),
    };

    if (!isEditMode) {
      payload.password = values.password;
      payload.confirm_password = values.confirm_password;
    }

    try {
      let userData;
      if (isEditMode) {
        userData = await updateUser(userId, payload);
        showAlert({ title: "Users Notification", message: userData?.message || "User updated successfully", variant: "success" });
      } else {
        if (loading) return;
        setLoading(true);
        userData = await addUsers(payload);
        showAlert({ title: "Users Notification", message: userData?.message || "User added successfully", variant: "success" });
      }
      const newUserId = userData?.data?.id;
      if (newUserId) {
        navigate('/users', {
          state: { selectUserId: newUserId },
          replace: true
        });
      } else {
        navigate('/users');
      }
    } catch (error) {
      setLoading(false);
      showAlert({
        title: "Error",
        message: error?.response?.data?.message || "Something went wrong",
        variant: "danger",
      });
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <Row className="align-items-end justify-content-between mb-4">
        <Col lg="7">
          <CustomBreadcrumb
            items={[
              { label: "My Team", href: "/users" },
              { label: isEditMode ? "Edit Team Member" : "Add Team Member", active: true },
            ]}
          />
          <PageTitle title={isEditMode ? "Edit Team Member" : "Add Team Member"} />
        </Col>
      </Row>
      <MainWrapper>
        <CardWrapper className="p-0">
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            // validateOnChange={false}
            // validateOnBlur={false}
            validationSchema={isEditMode ? editValidationSchema : validationSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, handleChange, errors, values }) => (

              <FormikForm>
                {console.log("error", errors, values)}
                <div className="p-4">
                  {isEditMode && <div className="d-flex align-items-start justify-content-between gap-4">
                    <h2 className="fs-5 fw-semibold text-gray-800 mb-0">Select User Type <span className="text-red-500">*</span></h2>
                    <Button
                      className="btn-md d-flex align-items-center gap-1"
                      variant="outline-danger"
                      onClick={() => setShowModal(userId)}
                    >
                      <Image
                        src="/images/trash-red.svg"
                        width={20}
                        height={20}
                        alt="Trash Red Icon"
                      />{" "}
                      Delete User
                    </Button>
                  </div>}
                  <Row className="align-items-center gy-4">
                    <Col xxl="2" xl="3" md="4" sm="6">
                      <div
                        className={`user-type-item bg-gray-50 rounded-4 px-3 py-4  ${values.userType === 3 ? "active" : ""
                          } ${isEditMode ? "disabled" : ""}`}
                        style={{ cursor: isEditMode ? "not-allowed" : "pointer", opacity: isEditMode && values.userType !== 3 ? 0.5 : 1 }}
                        onClick={() => {
                          if (!isEditMode) setFieldValue("userType", 3);
                        }}
                      >
                        <UserCircleCheck />
                        {/* <h3 className="h5 fw-semibold text-gray-700 mt-3 mb-1">
                          Expert ({(licences?.totalExpertLicence ?? 0) - (licences?.noOfExpertLicence ?? 0)}/{licences?.totalExpertLicence})
                        </h3> */}
                        <h3 className="h5 fw-semibold text-gray-700 mt-3 mb-1">
                          Expert ({(licences?.totalExpertLicence ?? 0) - (licences?.noOfExpertLicence ?? 0)}/{licences?.totalExpertLicence})
                        </h3>
                        <p className="mb-0">{licences?.noOfExpertLicence} License available</p>
                      </div>
                    </Col>

                    <Col xxl="2" xl="3" md="4" sm="6">
                      <div
                        className={`user-type-item bg-gray-50 rounded-4 px-3 py-4 ${values.userType === 5 ? "active" : ""
                          } ${isEditMode ? "disabled" : ""}`}
                        style={{ cursor: isEditMode ? "not-allowed" : "pointer", opacity: isEditMode && values.userType !== 5 ? 0.5 : 1 }}
                        onClick={() => {
                          if (!isEditMode) setFieldValue("userType", 5);
                        }}
                      >
                        <UserCircle />
                        <h3 className="h5 fw-semibold text-gray-700 mt-3 mb-1">
                          Staff ({(licences?.totalUserLicence ?? 0) - (licences?.noOfUserLicence ?? 0)}/{licences?.totalUserLicence})
                        </h3>
                        <p className="mb-0">{licences?.noOfUserLicence} License available</p>
                      </div>
                    </Col>

                    <Col xs={12}>
                      <ErrorMessage
                        name="userType"
                        component="div"
                        className="text-danger mt-2"
                      />
                    </Col>
                  </Row>
                </div>


                <div className="input-group-warpper p-4 mt-3">
                  <div className="input-group-item">
                    <h2 className="fs-5 fw-semibold text-gray-800 mb-4">
                      Personal Details
                    </h2>

                    <Row className="gy-4">
                      <Form.Group className="col-md-6" controlId="firstName">
                        <Form.Label className="required">First Name</Form.Label>
                        <Field
                          name="firstName"
                          className="form-control"
                          placeholder="Enter first name"
                        />
                        <ErrorMessage
                          name="firstName"
                          component="div"
                          className="text-danger"
                        />
                      </Form.Group>
                      <Form.Group className="col-md-6" controlId="lastName">
                        <Form.Label className="required">Last Name</Form.Label>
                        <Field
                          name="lastName"
                          className="form-control"
                          placeholder="Enter last name"
                        />
                        <ErrorMessage
                          name="lastName"
                          component="div"
                          className="text-danger"
                        />
                      </Form.Group>
                      <Form.Group className="col-md-6" controlId="email">
                        <Form.Label className="required">Email</Form.Label>
                        <Field
                          name="email"
                          className="form-control"
                          type="email"
                          disabled={isEditMode}
                          placeholder="Enter email"
                        />
                        <ErrorMessage
                          name="email"
                          component="div"
                          className="text-danger"
                        />
                      </Form.Group>
                      <Form.Group className="col-md-6" controlId="phone">
                        <Form.Label className="required">Phone</Form.Label>
                        <div style={{ position: "relative" }}>
                          <PhoneInput
                            className="form-control"
                            international
                            value={phone}
                            onChange={(e) => {
                              if (typeof e !== "undefined") {
                                changePhoneNumber(e);
                                setFieldValue("phone", e);
                              }
                            }}
                            placeholder="Enter phone number"
                          />

                        </div>
                        <ErrorMessage
                          name="phone"
                          component="div"
                          className="text-danger"
                        />
                      </Form.Group>

                      {!isEditMode && <Form.Group className="col-md-6" controlId="password">
                        <Form.Label className="required">Password</Form.Label>
                        <div className="input-warpper lock-icon">
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter your password"
                            name="password"
                            value={values.Password}
                            onChange={handleChange}
                            autoComplete="new-password"
                            isInvalid={!!errors.Password}
                          />
                          <button
                            type="button"
                            className="input-group-icon btn-reset right"
                            onClick={PasswordToggle}
                          >
                            <Image
                              src={showPassword ? "/images/eye.svg" : "/images/eye-off.svg"}
                              alt="Eye Icon"
                            />
                          </button>
                          <ErrorMessage
                            name="password"
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Form.Group>}
                      {!isEditMode && <Form.Group className="col-md-6" controlId="confirm_password">
                        <Form.Label className="required">Confirm Password</Form.Label>
                        <div className="input-warpper lock-icon">
                          <Form.Control
                            type={showConfirmPassword ? "text" : "password"}
                            onChange={handleChange}
                            placeholder="Enter your password"
                            name="confirm_password"
                          />
                          <button
                            type="button"
                            className="input-group-icon btn-reset right"
                            onClick={ConfirmPasswordToggle}
                          >
                            <Image
                              src={showConfirmPassword ? "/images/eye.svg" : "/images/eye-off.svg"}
                              alt="Eye Icon"
                            />
                          </button>
                          <ErrorMessage
                            name="confirm_password"
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Form.Group>}
                    </Row>
                  </div>
                </div>

                <div className="input-group-warpper p-4 mt-3">
                  {/* Address */}
                  <div className="input-group-item">

                    <h2 className="fs-5 fw-semibold text-gray-800 mb-4 d-flex align-items-center gap-2">
                      Address
                      <input
                        type="checkbox"
                        checked={addressDisabled}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setAddressDisabled(isChecked);

                          if (isChecked) {
                            // Use Admin's address data
                            setFieldValue("streetName", companyDetails?.address || "");
                            setFieldValue("zipCode", companyDetails?.zipCode || "");
                            setFieldValue("city", companyDetails?.city || "");

                            // Set country and state properly
                            const adminCountry = countryOptions.find(
                              (c) => c.isoCode === companyDetails?.country?.code
                            );
                            const adminState = State.getStatesOfCountry(adminCountry?.isoCode || "US").find(
                              (s) => s.name === companyDetails?.state?.name
                            );

                            setSelectedCountry(adminCountry || countryOptions[0]);
                            setSelectedState(companyDetails?.state?.name || "");

                            setFieldValue("country", adminCountry?.name || companyDetails?.country?.name || "");
                            setFieldValue("state", companyDetails?.state?.name || "");
                          } else {
                          
                            setFieldValue("streetName", "");
                            setFieldValue("zipCode", "");
                            setFieldValue("city", "");
                            setFieldValue("country", "");
                            setFieldValue("state", "");

                            setSelectedCountry(null);
                            setSelectedState(null);
                            setSelectedCity(null);
                          }
                        }}
                        style={{ cursor: "pointer" }}
                      />
                      <label style={{ cursor: "pointer", marginLeft: "8px" }}>
                        Make Same As Admin
                      </label>
                    </h2>

                    <Row className="gy-4">

                      {/* Street Name */}
                      <Form.Group className="col-md-12" controlId="streetName">
                        <Form.Label className="required">Street Name</Form.Label>
                        <Field
                          name="streetName"
                          className="form-control"
                          disabled={addressDisabled}
                          placeholder="Enter street name"
                        />
                        <ErrorMessage name="streetName" component="div" className="text-danger" />
                      </Form.Group>

                      {/* COUNTRY */}
                      <Form.Group className="col-md-6" controlId="country">
                        <Form.Label className="required">Country</Form.Label>

                        {addressDisabled ? (
                          <Field
                            name="country"
                            className="form-control"
                            disabled
                          />
                        ) : (
                          <Select
                            options={countryList}
                            value={selectedCountry}
                            onChange={(value) => {
                              setSelectedCountry(value);
                              setFieldValue("country", value.label);
                            }}
                            placeholder="Select Country"
                          />
                        )}

                        <ErrorMessage name="country" component="div" className="text-danger" />
                      </Form.Group>
                      {console.log('this is data for system value ------------------->', selectedCountry, selectedState)}
                      <Form.Group className="col-md-6" controlId="state">
                        <Form.Label className="required">State</Form.Label>

                        {addressDisabled ? (
                          <Field name="state" className="form-control" disabled />
                        ) : (
                          <Select
                            options={stateList}
                            value={selectedState}
                            isDisabled={!selectedCountry}
                            placeholder="Select State"
                            onChange={(value) => {
                              setSelectedState(value);
                              setFieldValue("state", value.label);
                              setFieldValue("city", "");
                            }}
                          />
                        )}

                        <ErrorMessage name="state" component="div" className="text-danger" />
                      </Form.Group>

                      <Form.Group className="col-md-6" controlId="city">
                        <Form.Label className="required">City</Form.Label>

                        {addressDisabled ? (
                          <Field name="city" className="form-control" disabled />
                        ) : (
                          <Select
                            options={cityList}
                            value={selectedCity}
                            isDisabled={!selectedState}
                            placeholder="Select City"
                            onChange={(value) => {
                              setSelectedCity(value);
                              setFieldValue("city", value.label);
                            }}
                          />
                        )}

                        <ErrorMessage name="city" component="div" className="text-danger" />
                      </Form.Group>

                      {/* ZIP CODE */}
                      <Form.Group className="col-md-6" controlId="zipCode">
                        <Form.Label className="required">Zip Code</Form.Label>
                        <Field
                          name="zipCode"
                          className="form-control"
                          disabled={addressDisabled}
                          placeholder="Enter zip code"
                        />
                        <ErrorMessage name="zipCode" component="div" className="text-danger" />
                      </Form.Group>
                    </Row>

                  </div>
                </div>

                <div className="input-group-warpper d-flex align-align-items-center gap-2 p-4 mt-3">
                  <Button variant="primary" className="btn-md" type="submit">
                    {!isEditMode ? 'Save & Add Team Member' : 'Save & Update Team Member'}
                  </Button>
                  <Button className="btn-md" variant="outline-secondary" type="reset" onClick={() => navigate('/users')}>
                    Cancel
                  </Button>
                </div>
              </FormikForm>
            )}
          </Formik>
        </CardWrapper>
      </MainWrapper>
      <Modal
        className="info-modal"
        show={!!showModal}
        onHide={() => setShowModal(null)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Body>
          <div className="text-center">
            <Image src="/images/delete-badge-red.svg" alt="Delete Badge" />
            <h2 className="fs-2 fw-semibold text-gray-700 mt-5 mb-2">
              Are you sure you want to delete this user?
            </h2>

            <div className="d-flex align-items-center gap-3">
              <Button
                variant="primary"
                onClick={(e) => deleteUserFunction(showModal)}
                className="btn w-100 mt-5"
              >
                Delete User
              </Button>
              <Button
                variant="gray"
                className="btn w-100 mt-5"
                onClick={() => setShowModal(null)}
              >
                Nope, Cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default UserForm;
